import { MARKET_LANGUAGE } from './constants';

// Returns approved vocab entries as a prompt injection string for a given market
function getVocabInjection(vocabulary, marketCode) {
  const approved = (vocabulary || []).filter(e => e.status === 'Approved');
  if (!approved.length) return '';
  const lines = approved
    .map(e => {
      const target = e.translations?.[marketCode];
      const source = e.termSE || e.term;
      if (!target || target === source) return null;
      return `• Använd alltid "${target}" (${marketCode}) — aldrig "${source}" eller andra alternativ`;
    })
    .filter(Boolean);
  if (!lines.length) return '';
  return `\n=== TERMINOLOGIKRAV (${marketCode}) ===\nDessa termer är obligatoriska — inga alternativ tillåtna:\n${lines.join('\n')}\n`;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Market code to full name mapping
const MARKET_CODE_MAP = {
  'SE': 'Sverige',
  'NO': 'Norge',
  'FI': 'Finland',
  'DE': 'Tyskland',
  'DK': 'Danmark',
  'NL': 'Nederländerna',
};

// Market-specific CTAs for meta descriptions
const MARKET_CTA = {
  'SE': ['Köp nu!', 'Beställ idag!', 'Handla nu!'],
  'NO': ['Bestill i dag!', 'Kjøp nå!', 'Handle nå!'],
  'FI': ['Tilaa nyt!', 'Osta heti!', 'Tilaa tänään!'],
  'DE': ['Jetzt kaufen!', 'Bestellen Sie jetzt!', 'Jetzt bestellen!'],
  'DK': ['Køb nu!', 'Bestil i dag!', 'Bestil nu!'],
  'NL': ['Koop nu!', 'Bestel vandaag!', 'Shop nu!'],
};

// Language-specific words to detect language mixing
const LANGUAGE_MARKERS = {
  'SE': ['vårdvardag', 'arbetsdag', 'komfort', 'kvalitet', 'och', 'för', 'med', 'dina', 'hela'],
  'NO': ['arbeidsdag', 'komfort', 'kvalitet', 'og', 'for', 'med', 'dine', 'hele', 'støtte'],
  'FI': ['työpäivä', 'mukavuus', 'laatu', 'ja', 'kanssa', 'sinun', 'koko', 'tuki'],
  'DE': ['arbeitstag', 'komfort', 'qualität', 'und', 'für', 'mit', 'ihre', 'ganzen'],
  'DK': ['arbejdsdag', 'komfort', 'kvalitet', 'og', 'for', 'med', 'dine', 'hele'],
  'NL': ['werkdag', 'comfort', 'kwaliteit', 'en', 'voor', 'met', 'jouw', 'hele', 'ondersteuning'],
};

export function buildPrompt(productName, category, features, market, brandGuidelines, categoryTemplates, vocabulary) {
  const vocabBlock = getVocabInjection(vocabulary, market);
  const featureList = features
    .split('\n')
    .map((f) => f.trim())
    .filter((f) => f.length > 0)
    .map((f) => `• ${f}`)
    .join('\n');
  
  const marketName = MARKET_CODE_MAP[market] || market;
  const { lang, instruction } = MARKET_LANGUAGE[marketName] || { lang: 'svenska', instruction: 'Skriv ENBART på svenska.' };
  const template = categoryTemplates[category] || '';

  return `Du är en professionell produktbeskrivningsskribent för Vårdväskan, ett ledande svenskt e-handelsföretag inom sjukvårdskläder och medicinsk utrustning.

=== VARUMÄRKESRIKTLINJER ===
${brandGuidelines}
${vocabBlock}
=== KATEGORIMALL: ${category.toUpperCase()} ===
${template}

=== MARKNADSANPASSNING ===
Skriv för ${marketName}-marknaden. ${instruction} Anpassa ton och kulturella referenser för ${marketName}.

=== PRODUKTINFORMATION ===
Produktnamn: ${productName}
Kategori: ${category}
Viktiga egenskaper:
${featureList}

=== OBLIGATORISKA KRAV ===
- SPRÅK: ${instruction} Hela texten – inklusive specifikationer och avslutning – måste vara på ${lang}.
- ORDANTAL: Din text MÅSTE vara mellan 150 och 220 ord. Minimum 150 ord är obligatoriskt — om du är under 150, expandera Steg 2 och Steg 3. Verifiera: word_count = len(text.split()); assert 150 <= word_count <= 220. Svara INTE förrän verifierat. Sektionsmål: Steg 1 = 40 ord, Steg 2 = 70 ord, Steg 3 = 90 ord, Steg 4 = 35 ord, Steg 5 = 25 ord.
- Professionell men levande och engagerande ton
- Direkt tilltal (du/dig eller marknadsekvivalent), inga hashtags, inga kampanjfraser
- INGA markdown-tecken: inga **, inga *, inga #, inga backticks, inga kod-block
- Specs-blocket (Steg 4): ren text, ett fält per rad
- Ren löptext — stycken separerade med blankrad

Skriv produktbeskrivningen nu (ren text, inga markdown-tecken):`;
}

// Build prompt for Norce export with STRICT character/word count requirements
export function buildNorcePrompt(productName, category, bullets, marketCode, previousErrors = null, brandGuidelines, categoryTemplates, vocabulary) {
  const vocabBlock = getVocabInjection(vocabulary, marketCode);
  const bulletList = bullets
    .split('\n')
    .map((f) => f.trim())
    .filter((f) => f.length > 0)
    .map((f) => `• ${f}`)
    .join('\n');
  
  const marketName = MARKET_CODE_MAP[marketCode] || 'Sverige';
  const { lang, instruction } = MARKET_LANGUAGE[marketName] || { lang: 'svenska', instruction: 'Skriv ENBART på svenska.' };
  const template = categoryTemplates[category] || '';
  // OBS: aldrig fallback till svenska — hellre skippa CTA-injektion än få fel språk.
  const ctas = MARKET_CTA[marketCode] || [];

  // Compute guidance body range for the prompt (actual enforcement is done post-generation by enforceMetaDescLength)
  const ctaLen = (ctas[0] || '').length;
  const bodyMin = 149 - ctaLen;   // combined min: bodyMin + 1 + ctaLen = 150
  const bodyMax = 159 - ctaLen;   // combined max: bodyMax + 1 + ctaLen = 160

  let errorFeedback = '';
  if (previousErrors && previousErrors.length > 0) {
    errorFeedback = `
=== ⚠️ TIDIGARE FÖRSÖK MISSLYCKADES ===
${previousErrors.join('\n')}

DU MÅSTE KORRIGERA DESSA FEL! Räkna tecken/ord MYCKET noggrant denna gång.
`;
  }

  return `Du är en professionell produktbeskrivningsskribent och SEO-specialist för Vårdväskan.
${errorFeedback}
=== MARKNADSANPASSNING ===
Marknad: ${marketName} (${marketCode})
Språk: ${lang}
${instruction}

KRITISKT: Skriv 100% på ${lang}. INGEN blandning av språk tillåten!

=== VARUMÄRKESRIKTLINJER ===
${brandGuidelines}
${vocabBlock}=== KATEGORIMALL: ${category.toUpperCase()} ===
${template}

=== PRODUKTINFORMATION ===
Produktnamn: ${productName}
Kategori: ${category}
Råmaterial (bullets):
${bulletList || '(ingen information given — skriv generisk text för kategorin)'}

=== UPPGIFT: GENERERA TRE SAKER ===

1. DESCRIPTION (produktbeskrivning — OBLIGATORISKT HTML-FORMAT)

   Skriv beskrivningen i EXAKT detta HTML-format:
   <p>[Hook-stycke: 2-3 meningar som öppnar produkttexten.]<br /><br />[Fördjupning: 2-3 meningar om material, konstruktion, användning — baserat på Bullets-fältet.]<br /><br />• [Bullet 1 — från Bullets-fältet]<br />• [Bullet 2 — från Bullets-fältet]<br />• [Bullet 3 — från Bullets-fältet]<br />• [Bullet 4 — från Bullets-fältet]<br />• [Bullet 5 — från Bullets-fältet]<br /><br /><em>[Eventuell disclaimer eller avslutande mening i kursiv.]</em></p>

   HTML-REGLER (STRIKT):
   - Allt innehåll i EN <p>-tagg
   - Använd <br /><br /> mellan stycken — ALDRIG \\n\\n eller tomma rader
   - Använd • (bullet-tecken + mellanslag) för varje punkt, med <br /> emellan
   - Använd <em> för disclaimer/avslutning
   - Tillåtna taggar: <p>, <br />, <em>, <strong> — INGA andra
   - INGA <ul>, <li>, <h1>-<h6>, <a href> eller andra taggar
   - Bullet-punkterna MÅSTE hämtas från Bullets-fältet — hitta INTE på egna

   OBLIGATORISKA KRAV FÖR PRODUKTBESKRIVNING:
   - ORDANTAL (synlig text, HTML-taggar räknas EJ): STRIKT 150-220 ord (exkl. bullets och disclaimer)
     FÖRDELNING: Hook ~50 ord, Fördjupning ~100 ord, Avslutning ~30 ord
     Räkna: strip_tags(text) → word_count; assert 150 <= word_count <= 220
     Gå INTE under 150 ord. Gå INTE över 220 ord.
     KRITISKT: Om din räkning ger < 150 ord — SKRIV OM och expandera hook/fördjupning tills du når 150 ord.
     Varje mening bidrar till ordantalet — lägg till konkreta detaljer från Bullets-fältet.
   - Om under 150 ord: expandera hook och fördjupningsstycket med fler konkreta detaljer
   - Om över 220 ord: korta ned — ta bort upprepningar och generiska fraser
   - ${instruction}
   - ÖPPNINGSMENING: MÅSTE vara specifik för just denna produkt och dessa egenskaper.
     FÖRBJUDET: "Som sjuksköterska behöver du...", "Inom vården är det viktigt att...",
     "En pålitlig partner för din arbetsvardag", "Perfekt för dig som jobbar inom vården".
     RÄTT: börja med produktnamnet, en konkret egenskap eller en specifik användningssituation.
   - INGA UPPREPNINGAR: Varje mening MÅSTE tillföra ny, unik faktainformation.
     Samma fördel får INTE uttryckas i omskrivna ord i ett annat stycke.
   - INGA GENERISKA FRASER: Strikt förbjudet:
     "ger dig den komfort du förtjänar", "pålitlig partner för din arbetsvardag",
     "det självklara valet för vårdpersonal", "stöd hela dagen" (om ej faktabaserat),
     "perfekt för dig som" (generisk öppning), "dina ben förtjänar det bästa".
     Skriv KONKRET och FAKTABASERAT — varje påstående ska ha stöd i Bullets-fältet.
   - BULLETS (STRIKT): Hämtas DIREKT och EXAKT från Viktiga egenskaper — inga tillägg,
     inga omskrivningar, inga förstärkningsord, inga påhittade egenskaper.
     Om ett bullet saknar värde — utelämna det hellre än att uppfinna något.
   - HALLUCINERINGSFÖRBUD: Nämn ALDRIG material, egenskaper eller specifikationer som INTE finns i Bullets-fältet ovan. Om du är osäker — utelämna det.

2. META_TITLE (SEO-titel)
   - MÅL: EXAKT 50-55 TECKEN — räkna varje tecken med len()
   - Inkludera produktnamn + en nytta/fördel
   - Om produktnamnet är långt, förkorta kreativt
   - ALDRIG bara produktnamnet — lägg till en fördel!
   - ${instruction}
   - Format: "Produktnamn – Kort fördel" eller "Produktnamn | Nytta"
   - BRA EXEMPEL (54 tecken): "Vera Prikker Rosa Støttestrømpe – Komfort hele skiftet"
     len("Vera Prikker Rosa Støttestrømpe – Komfort hele skiftet") = 54 ✓
   - FÖR KORT (46 tecken) — INTE OK: "Vera Prikker Rosa Støttestrømpe – Piggere ben!"
     → Lägg till fler ord tills du når 50-55 tecken
   - FÖR LÅNG (58 tecken) — INTE OK: "Vera Prikker Rosa Støttestrømpe – Piggere ben på jobb!"
     → Korta ner tills du når 50-55 tecken
   - RÄKNA TECKEN NOGA INNAN DU SVARAR!
   - HALLUCINERINGSFÖRBUD: Nämn ALDRIG material, egenskaper eller specifikationer som INTE finns i Bullets-fältet ovan.

3. META_DESCRIPTION (SEO-beskrivning)
   MÅL: EXAKT 150-160 TECKEN TOTALT (body + " " + CTA)

   CTA för denna marknad: "${ctas[0]}" (${ctaLen} tecken)
   Din body ska vara ungefär ${bodyMin}-${bodyMax} tecken, sedan lägg till " ${ctas[0]}" sist.

   Steg 1 — Skriv body: ~${bodyMin}-${bodyMax} tecken. Sammanfatta produktens fördelar.
   Steg 2 — Avsluta med: " ${ctas[0]}"
   Steg 3 — Kontrollera: len(body + " ${ctas[0]}") ska vara 150-160

   - ${instruction}
   - HALLUCINERINGSFÖRBUD: Nämn ALDRIG material, egenskaper eller specifikationer som INTE finns i Bullets-fältet ovan.
   - RÄKNA TECKEN NOGA!

=== SVARFORMAT ===
Svara EXAKT i detta format:

---DESCRIPTION---
<p>[Hook 2-3 meningar]<br /><br />[Fördjupning 2-3 meningar]<br /><br />• [Bullet 1]<br />• [Bullet 2]<br />• [Bullet 3]<br />• [Bullet 4]<br />• [Bullet 5]<br /><br /><em>[Disclaimer/avslutning]</em></p>

---META_TITLE---
[SEO-titel, 50-55 tecken, räkna noga]

---META_DESCRIPTION---
[body (${bodyMin}-${bodyMax} tecken) + " " + "${ctas[0]}" = 150-160 tecken totalt]

=== VERIFIERING INNAN DU SVARAR ===
1. Räkna ord i description (HTML-taggar ej inräknade): strip tags → assert 150 <= len(text.split()) <= 220
2. Räkna tecken i meta_title: assert 50 <= len(meta_title) <= 55
3. Räkna tecken i body (utan CTA): assert ${bodyMin} <= len(body) <= ${bodyMax}
4. Räkna tecken i kombinerad meta_description: assert 150 <= len(body + " " + "${ctas[0]}") <= 160
5. Kontrollera att ALL text är på ${lang} — inga ord från andra språk!
6. Kontrollera att meta_description slutar med rätt CTA
7. Kontrollera att INGA material/egenskaper nämns som saknas i Bullets
8. Kontrollera att öppningsmeningen är specifik för DENNA produkt — inte generisk
9. Kontrollera att INGEN mening upprepar en fördel som redan nämnts
10. Kontrollera att INGA generiska fraser förekommer

SVARA INTE FÖRRÄN ALLA VERIFIERINGAR PASSERAR!`;
}

export function cleanOutput(text, productName = '') {
  let clean = text
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/`{1,3}/g, '')
    .replace(/^#+\s+/gm, '')
    .replace(/^[-–]\s+/gm, '')
    .replace(/_{2}/g, '')
    .trim();
  const firstLine = clean.split('\n')[0].trim();
  if (productName && firstLine.toLowerCase() === productName.trim().toLowerCase()) {
    clean = clean.slice(firstLine.length).trimStart();
  }
  return clean;
}

// Market-neutral filler phrases to pad short meta descriptions (no product facts fabricated)
// Rotated randomly per product. All phrases verified and approved.
const META_FILLERS = {
  SE: [
    " Perfekt för sjukvårdspersonal.",
    " God komfort hela arbetspasset.",
    " För dig som jobbar inom vården.",
    " Perfekt val bland vårdpersonal.",
    // Shorter fillers for edge-cases where body is 136-141 chars
    " Prova gärna.",
    " I lager nu.",
    " Finns nu.",
    " Välj rätt.",
  ],
  NO: [
    " Perfekt for helsepersonell nå.",
    " Kjært av nordiske sykepleiere.",
    " God komfort hele arbeidsvakten.",
    " Perfekt for alt helsepersonell.",
    " På lager nå.",
    " Prøv nå.",
    " Bestill nå.",
  ],
  FI: [
    " Hoitajien suosikki Pohjolassa.",
    " Sopii koko hoitoalan päivälle.",
    " Hoitoalan oma suosikkivaruste.",
    " Pohjoinen valinta hoitoalalle.",
    " Kokeile nyt.",
    " Saatavilla nyt.",
    " Tilaa nyt.",
  ],
  DE: [
    " Perfekt für alle Pflegekräfte.",
    " Beliebt bei Pflegefachkräften.",
    " Komfort für den ganzen Dienst.",
    " Perfekt für das Pflegepersonal.",
    " Verfügbar.",
    " Jetzt entdecken.",
    " Sofort lieferbar.",
  ],
  DK: [
    " Perfekt til plejepersonale nu.",
    " God komfort hele arbejdsdagen.",
    " For dig som arbejder i plejen.",
    " Elsket af dansk plejepersonale.",
    " På lager nu.",
    " Prøv nu.",
    " Bestil nu.",
  ],
  NL: [
    " Perfect voor zorgprofessionals.",
    " Voor iedereen die in de zorg werkt.",
    " Uitstekend comfort gedurende de hele dienst.",
    " Geliefd bij zorgpersoneel.",
    " Nu op voorraad.",
    " Probeer nu.",
    " Bestel nu.",
  ],
};

// Enforce meta description to 150-160 chars by trimming (too long) or padding (too short).
// Padding uses market-neutral phrases that don't fabricate product-specific facts.
function enforceMetaDescLength(metaDesc, marketCode) {
  const ctas = MARKET_CTA[marketCode];
  // OBS: aldrig fallback till svenska CTA — hellre lämna model-output orörd
  // än injicera fel språk. Om marknaden saknar CTA-lista → returnera normaliserad text.
  if (!ctas || ctas.length === 0) {
    return metaDesc.replace(/\s+/g, ' ').trim();
  }
  // Collect CTAs from ALL markets so we can detect model-generated ones in any language
  const allCtas = [...new Set(Object.values(MARKET_CTA).flat())];
  const MIN = 150;
  const MAX = 160;

  // Normalise whitespace first (fixes trailing-space CTA detection failures)
  const normalized = metaDesc.replace(/\s+/g, ' ').trim();

  if (normalized.length >= MIN && normalized.length <= MAX) return normalized;

  // Split off the CTA suffix if already present
  // Check current-market CTAs first, then all-market CTAs
  let body = normalized;
  let ctaFound = false;
  for (const c of [...ctas, ...allCtas]) {
    const candidate = c.trim();
    if (body.endsWith(candidate)) {
      body = body.slice(0, body.length - candidate.length).trimEnd();
      ctaFound = true;
      break;
    }
  }
  const cta = ctas[0]; // always append current-market CTA

  const maxBody = MAX - 1 - cta.length;
  const minBody = MIN - 1 - cta.length;

  if (body.length > maxBody) {
    // TOO LONG: trim at last sentence or word boundary — never mid-word
    const trimmed = body.slice(0, maxBody);
    const lastDot   = Math.max(trimmed.lastIndexOf('. '), trimmed.lastIndexOf('! '), trimmed.lastIndexOf('? '));
    const lastComma = trimmed.lastIndexOf(', ');
    const lastSpace = trimmed.lastIndexOf(' ');

    // Prefer sentence boundary if it's in the latter half of trimmed
    if (lastDot > maxBody * 0.5) {
      body = trimmed.slice(0, lastDot + 1).trimEnd();
    } else if (lastComma > maxBody * 0.5) {
      body = trimmed.slice(0, lastComma).trimEnd();
    } else if (lastSpace > 0) {
      body = trimmed.slice(0, lastSpace).trimEnd();
    } else {
      body = trimmed.trimEnd();
    }
  } else if (body.length < minBody) {
    // TOO SHORT: pad with a randomly selected market-specific filler.
    // OBS: aldrig fallback till svenska — hellre lämna oförändrat än få fel språk.
    const fillers = META_FILLERS[marketCode];
    if (!fillers || fillers.length === 0) {
      // Okänd marknad utan filler-lista → returnera as-is utan padding
      const asIs = body + ' ' + cta;
      return asIs.length <= MAX ? asIs : asIs.slice(0, MAX);
    }
    const shuffled = [...fillers].sort(() => Math.random() - 0.5);
    let padded = false;
    for (const filler of shuffled) {
      const candidate = body + filler;
      const combined = candidate + ' ' + cta;
      if (combined.length >= MIN && combined.length <= MAX) {
        body = candidate;
        padded = true;
        break;
      }
    }
    if (!padded) {
      // Pick the filler that gets closest to MIN without going way over
      for (const filler of shuffled) {
        const candidate = body + filler;
        if ((candidate + ' ' + cta).length >= MIN) {
          body = candidate;
          break;
        }
      }
    }
  }

  const result = body + ' ' + cta;
  // Hard clamp — ensure result never exceeds MAX, always cut at word boundary
  if (result.length > MAX) {
    const maxBodyFinal = MAX - 1 - cta.length;
    const trimmed = body.slice(0, maxBodyFinal);
    const lastSpace = trimmed.lastIndexOf(' ');
    const safeTrimmed = lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed;
    const finalResult = safeTrimmed.trimEnd() + ' ' + cta;
    // Safety: if hard clamp trimmed too aggressively (< MIN), return normalized as last resort
    if (finalResult.length < MIN) return normalized.length <= MAX ? normalized : normalized.slice(0, MAX);
    return finalResult;
  }
  // Safety: if padding logic somehow still produced < MIN, return normalized as last resort
  if (result.length < MIN) return normalized.length <= MAX ? normalized : normalized.slice(0, MAX);
  return result;
}

// Parse Norce response into three parts
function parseNorceResponse(text, marketCode) {
  const result = {
    description: '',
    metaTitle: '',
    metaDescription: '',
  };
  
  // Extract description — keep HTML tags as-is, do NOT run cleanOutput
  const descMatch = text.match(/---DESCRIPTION---\s*([\s\S]*?)(?=---META_TITLE---|$)/i);
  if (descMatch) {
    result.description = descMatch[1].trim();
  }
  
  // Extract meta title - clean up any extra whitespace or newlines
  const titleMatch = text.match(/---META_TITLE---\s*([\s\S]*?)(?=---META_DESCRIPTION---|$)/i);
  if (titleMatch) {
    result.metaTitle = titleMatch[1].trim().replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
  }
  
  // Extract meta description and enforce 150-160 char length
  const metaDescMatch = text.match(/---META_DESCRIPTION---\s*([\s\S]*?)$/i);
  if (metaDescMatch) {
    const raw = metaDescMatch[1].trim().replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    result.metaDescription = enforceMetaDescLength(raw, marketCode);
  }
  
  return result;
}

// Verify meta title and description meet requirements
function verifyMeta(title, description, descriptionText, marketCode) {
  const errors = [];
  
  // Meta title verification (50-55 chars)
  const titleLen = title.length;
  if (titleLen < 50) {
    errors.push(`Meta title för kort: ${titleLen} tecken (min 50, behöver ${50 - titleLen} till)`);
  }
  if (titleLen > 55) {
    errors.push(`Meta title för lång: ${titleLen} tecken (max 55, ta bort ${titleLen - 55})`);
  }
  
  // Meta description verification (145-160 chars)
  // 145 is the practical minimum after post-processing; 160 is enforced by the enforcer
  const descLen = description.length;
  if (descLen < 145) {
    errors.push(`Meta description för kort: ${descLen} tecken (min 145, behöver ${145 - descLen} till)`);
  }
  if (descLen > 160) {
    errors.push(`Meta description för lång: ${descLen} tecken (max 160, ta bort ${descLen - 160})`);
  }
  
  // Description word count verification (150-220 words) — strip HTML tags first
  const textOnly = descriptionText.replace(/<[^>]+>/g, ' ').replace(/&[a-zA-Z]+;/g, ' ');
  const wordCount = textOnly.split(/\s+/).filter(w => w.length > 0).length;
  if (wordCount < 150) {
    errors.push(`Description för kort: ${wordCount} ord (min 150, behöver ${150 - wordCount} ord till)`);
  }
  if (wordCount > 220) {
    errors.push(`Description för lång: ${wordCount} ord (max 220, ta bort ${wordCount - 220} ord)`);
  }
  
  // Check for CTA in meta description — skip check if market saknar CTA-lista
  const ctas = MARKET_CTA[marketCode];
  if (ctas && ctas.length > 0) {
    const hasCTA = ctas.some(cta => description.toLowerCase().includes(cta.toLowerCase().replace('!', '')));
    if (!hasCTA) {
      errors.push(`Meta description saknar CTA. Lägg till: ${ctas.join(' eller ')}`);
    }
  }
  
  return errors;
}

// Call unified AI backend — routes to Gemini, Claude, or GPT-4o based on model
async function callAI(prompt, model = 'gemini') {
  const res = await fetch(`${BACKEND_URL}/api/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model }),
  });

  if (!res.ok) {
    let detail = res.status;
    try { const err = await res.json(); detail = err.detail || err.error || res.status; } catch (_) {}
    throw new Error(`API-fel: ${detail}`);
  }

  const data = await res.json();
  if (!data.text) throw new Error('Tom respons från API');
  // Return full data so callers can extract token counts for cost tracking
  return data;
}

// Map display market name to market code (for single Generate page → Norce flow)
const MARKET_DISPLAY_TO_CODE_MAP = {
  'Sverige': 'SE', 'Norge': 'NO', 'Finland': 'FI',
  'Tyskland': 'DE', 'Danmark': 'DK', 'Frankrike': 'FR',
  'Spanien': 'ES', 'Nederländerna': 'NL', 'Polen': 'PL',
};

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export async function generateOne(productName, category, features, market, ctx = {}) {
  const { brandGuidelines = '', categoryTemplates = {}, vocabulary = [], model = 'gemini' } = ctx;
  const prompt = buildPrompt(productName, category, features, market, brandGuidelines, categoryTemplates, vocabulary);
  const data = await callAI(prompt, model);
  return {
    text: cleanOutput(data.text, productName),
    usage: { inputTokens: data.input_tokens || 0, outputTokens: data.output_tokens || 0, model: data.model || model },
  };
}

// Generate Norce output with RETRY LOGIC for verification
export async function generateNorce(productName, category, bullets, marketCode, ctx = {}) {
  const { brandGuidelines = '', categoryTemplates = {}, vocabulary = [], model = 'gemini' } = ctx;
  
  const MAX_RETRIES = 5;
  let lastErrors = null;
  let bestResult = null;
  let bestScore = -Infinity;
  let bestUsage = { inputTokens: 0, outputTokens: 0, model };
  
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    console.log(`[Norce] Attempt ${attempt}/${MAX_RETRIES} for ${productName} (${marketCode})`);
    
    const prompt = buildNorcePrompt(productName, category, bullets, marketCode, lastErrors, brandGuidelines, categoryTemplates, vocabulary);
    
    try {
      const data = await callAI(prompt, model);
      const result = parseNorceResponse(data.text, marketCode);
      const usage = { inputTokens: data.input_tokens || 0, outputTokens: data.output_tokens || 0, model: data.model || model };
      
      const errors = verifyMeta(result.metaTitle, result.metaDescription, result.description, marketCode);
      
      const titleLen = result.metaTitle.length;
      const descLen = result.metaDescription.length;
      const wordCount = result.description.split(/\s+/).filter(w => w.length > 0).length;
      
      let score = 0;
      if (titleLen < 50) score -= (50 - titleLen) * 2;
      if (titleLen > 55) score -= (titleLen - 55) * 2;
      if (descLen < 150) score -= (150 - descLen);
      if (descLen > 160) score -= (descLen - 160);
      if (wordCount < 150) score -= (150 - wordCount) * 0.5;
      if (wordCount > 220) score -= (wordCount - 220) * 0.5;
      
      console.log(`[Norce] Attempt ${attempt} score: ${score}, errors: ${errors.length}`);
      
      if (score > bestScore) {
        bestScore = score;
        bestResult = result;
        bestUsage = usage;
      }
      
      if (errors.length === 0) {
        console.log(`[Norce] ✓ Verification passed on attempt ${attempt}`);
        return { ...result, usage };
      }
      
      lastErrors = errors;
      if (attempt < MAX_RETRIES) await sleep(500);
      
    } catch (err) {
      console.error(`[Norce] Attempt ${attempt} failed:`, err.message);
      lastErrors = [`API-fel: ${err.message}`];
      if (attempt === MAX_RETRIES) throw err;
      // Rate limit-fel behöver mer återhämtningstid än vanliga verifieringsfel
      const isRateLimit = /429|rate limit/i.test(err.message);
      if (isRateLimit) await sleep(4000);
    }
  }
  
  console.log(`[Norce] ⚠️ Returning best result after ${MAX_RETRIES} attempts (score: ${bestScore})`);
  return { ...bestResult, needsReview: true, usage: bestUsage };
}


// Generate all three fields (description + meta title + meta desc) for a single product.
// Accepts the same market display name as generateOne but uses the Norce prompt internally.
export async function generateAllFields(productName, category, features, market, ctx = {}) {
  const marketCode = MARKET_DISPLAY_TO_CODE_MAP[market] || market;
  return generateNorce(productName, category, features, marketCode, ctx);
}


// Source language code → full language name
const SOURCE_LANG_MAP = {
  'SE': 'svenska',
  'NO': 'norska (bokmål)',
  'FI': 'finska',
  'DE': 'tyska',
  'DK': 'danska',
  'NL': 'nederländska (Nederländerna)',
  'EN': 'engelska',
};

// Brand-aware translation prompt
function buildTranslationPrompt(sourceText, sourceLangCode, targetMarketCode, extraContext, brandGuidelines, vocabulary) {
  const vocabBlock = getVocabInjection(vocabulary || [], targetMarketCode);
  const sourceLang = SOURCE_LANG_MAP[sourceLangCode] || 'svenska';
  const targetMarketName = MARKET_CODE_MAP[targetMarketCode] || targetMarketCode;
  const { lang: targetLang, instruction } = MARKET_LANGUAGE[targetMarketName] || { lang: 'svenska', instruction: 'Skriv ENBART på svenska.' };

  return `Du är en expert copywriter och översättare för Vårdväskan – ett e-handelsföretag som säljer arbetskläder och medicinsk utrustning till sjukvårdspersonal i Norden och Europa.

## Varumärkesriktlinjer
${brandGuidelines}
${vocabBlock}
## Din uppgift
Översätt följande text från ${sourceLang} till ${targetLang} för marknaden ${targetMarketName}.

VIKTIGT:
- Detta är INTE maskintranslation. Anpassa texten kulturellt och terminologiskt för ${targetMarketName}.
- ${instruction}
- Bevara den ursprungliga textens struktur, längd och ton.
- Använd rätt branschterminologi för vård och medicinsk utrustning på ${targetLang}.
- Behåll varumärkets ton: varm, professionell, direkt tilltal (du/dig i passande form för marknaden).
- Skriv ENBART översättningen — inga förklaringar, ingen kommentar, ingen inledning.
${extraContext ? `\n## Ytterligare instruktioner\n${extraContext}` : ''}

## Text att översätta
${sourceText}

## Översättning till ${targetLang}`;
}

// Translate text to a single market — no retry needed for translation
export async function translateText(sourceText, sourceLangCode, targetMarketCode, extraContext = '', ctx = {}) {
  const { brandGuidelines = '', vocabulary = [], model = 'gemini' } = ctx;
  const prompt = buildTranslationPrompt(sourceText, sourceLangCode, targetMarketCode, extraContext, brandGuidelines, vocabulary);
  const data = await callAI(prompt, model);
  return {
    text: cleanOutput(data.text.trim()),
    usage: { inputTokens: data.input_tokens || 0, outputTokens: data.output_tokens || 0, model: data.model || model },
  };
}
